import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { Field } from 'redux-form';
import Autosuggest from 'react-autosuggest';
import AutosuggestHighlightParse from 'autosuggest-highlight/parse';

import { ApiService } from 'services';

const wordCharacterRegex = /[a-z0-9_]/i;
const whitespacesRegex = /\s+/;

const WAIT_INTERVAL = 500;

// https://developer.mozilla.org/en/docs/Web/JavaScript/Guide/Regular_Expressions#Using_Special_Characters
function escapeRegexCharacters(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getSuggestions(category, context, value, suggestionsCallback) {
  const escapedValue = escapeRegexCharacters(value?.trim() || '');

  if (escapedValue === '') {
    suggestionsCallback([]);
    return;
  }

  if (category === '' || category === null) {
    suggestionsCallback([]);
    return;
  }

  const postData = {
    type: category,
    valueEntered: value,
  };

  if (context != null) {
    postData.context = context;
  }

  ApiService
    .lookup(postData)
    .then((result) => {
      suggestionsCallback(result.data);
    })
    .catch(err => suggestionsCallback([]));
}

function extend(subject, baseObject) {
  subject = subject || {};
  Object.keys(subject).forEach((key) => {
    if (key != null) {
      baseObject[key] = !!subject[key];
    }
  });
  return baseObject;
}

function findMatchesToHighlight(text, query) {
  const options = extend({}, {
    insideWords: true,
    findAllOccurrences: true,
    requireMatchAll: false
  });

  return (
    query
      .trim()
      .split(whitespacesRegex)
      // If query is blank, we'll get empty string here, so let's filter it out.
      .filter(word => word.length > 0)
      .reduce((result, word) => {
        const wordLen = word.length;
        const prefix = !options.insideWords && wordCharacterRegex.test(word[0]) ? '\\b' : '';
        const regex = new RegExp(prefix + escapeRegexCharacters(word), 'i');
        let occurrence;
        let index;

        occurrence = regex.exec(text);
        if (options.requireMatchAll && occurrence === null) {
          text = '';
          return [];
        }

        while (occurrence) {
          // eslint-disable-next-line
          index = occurrence.index;

          result.push([index, index + wordLen]);

          // Replace what we just found with spaces so we don't find it again.
          text = text.slice(0, index)
            + new Array(wordLen + 1).join(' ')
            + text.slice(index + wordLen);

          if (!options.findAllOccurrences) {
            break;
          }

          occurrence = regex.exec(text);
        }
        return result;
      }, [])
      .sort((match1, match2) => match1[0] - match2[0])
  );
}

function renderSuggestion(suggestion, { query }) {
  const suggestionText = `${ suggestion.value }`;
  const matches = findMatchesToHighlight(suggestionText, query);
  const parts = AutosuggestHighlightParse(suggestionText, matches);

  return (
    <span className="suggestion-content">
      <span className="name">
        {
          parts != null && parts
            .map((part, index) => {
              if (part != null) {
                const className = part.highlight ? 'highlight' : null;
                return (
                  // eslint-disable-next-line
                  <span className={ className } key={ index }>{ part.text }</span>
                );
              }
              return null;
            })
        }
      </span>
    </span>
  );
}

const AutoSuggestWrapper = ({ input, ...custom }) => <Autosuggest
  getSuggestionValue={ (suggestion) => {
    input.onChange(suggestion.value);
    return suggestion.value;
  } }
  { ...custom }
/>;

AutoSuggestWrapper.propTypes = {
  input: PropTypes.shape({
    onChange: PropTypes.func
  }).isRequired
};

export class AutoCompleteInput extends Component {
  static propTypes = {
    input: PropTypes.shape(),
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object
    ]),
    className: PropTypes.string,
    required: PropTypes.bool,
    minCharacters: PropTypes.number,
    category: PropTypes.string.isRequired,
    context: PropTypes.shape({
      type: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired
    }),
    onSelectedItem: PropTypes.func,
    onChange: PropTypes.func,
    disabled: PropTypes.bool
  };

  static defaultProps = {
    input: null,
    name: null,
    placeholder: 'Start typing...',
    value: '',
    className: 'form-input',
    required: false,
    minCharacters: 3,
    context: null,
    onSelectedItem: null,
    onChange: null,
    disabled: false
  };

  static getAutoCompleteText(id) {
    switch (id) {
      case 0:
        return 'client';
      case 1:
        return 'legalentity';
      case 2:
        return 'customer';
      case 5:
        return 'account';
      case 9:
        return 'user';
      default:
        return '';
    }
  }

  constructor(props) {
    super(props);

    let currentValue = '';
    if (props.value && typeof props.value === 'object') {
      if (typeof props.value.value !== 'undefined') {
        currentValue = props.value.value;
      }
    } else if (props.value && typeof props.value === 'string') {
      currentValue = props.value ?? '';
    }

    this.state = {
      currentSelection: props.value,
      currentValue,
      currentContext: props.context,
      suggestions: []
    };

    this.setSuggestionsState = this.setSuggestionsState.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    if (JSON.stringify(props.context) !== JSON.stringify(state.currentContext)) {
      return {
        currentContext: props.context,
        currentSelection: null,
        currentValue: '',
      };
    }
    if (props.value && typeof props.value === 'object') {
      if (JSON.stringify(props.value) !== JSON.stringify(state.currentSelection)) {
        let currentValue = '';
        if (typeof props.value.value !== 'undefined') {
          currentValue = props.value.value ?? '';
        } else {
          currentValue = props.value ?? '';
        }
        return {
          currentSelection: props.value,
          currentValue
        };
      }
    } else if (props.value && typeof props.value === 'string') {
      if (JSON.stringify(props.value) !== JSON.stringify(state.currentValue)) {
        let currentValue = '';
        if (typeof props.value.value !== 'undefined') {
          currentValue = props.value.value ?? '';
        } else {
          currentValue = props.value ?? '';
        }
        return {
          currentSelection: props.value,
          currentValue
        };
      }
    } else if (props.disabled || (!props.value && state.currentSelection)) {
      return {
        currentSelection: null,
        currentValue: ''
      };
    }
    return null;
  }

  onChange = (event, { newValue }) => {
    const { onChange } = this.props;
    const { suggestions } = this.state;

    if (suggestions && suggestions.length > 0) {
      if (suggestions.filter(s => s.value === newValue).length > 0) {
        return;
      }
    }

    this.setState({
      currentValue: newValue,
    });

    if (onChange != null) {
      onChange(event, newValue);
    }
  };

  onSuggestionSelected = (event, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }) => {
    const { onSelectedItem } = this.props;
    this.setState({
      currentValue: suggestionValue,
    });

    if (onSelectedItem) {
      if (suggestion.type === 1) {
        onSelectedItem({
          id: suggestion.id,
          type: AutoCompleteInput.getAutoCompleteText(suggestion.type),
          value: suggestion.value,
          chapter3Status: suggestion.chapter3Status,
          chapter4Status: suggestion.chapter4Status,
          crsParticipating: suggestion.crsParticipating,
          chapter4StatusDescription: suggestion.chapter4StatusDescription,
          chapter3StatusDescription: suggestion.chapter3StatusDescription
        });
      } else {
        onSelectedItem({
          id: suggestion.id,
          type: AutoCompleteInput.getAutoCompleteText(suggestion.type),
          value: suggestion.value
        });
      }
    }
  };

  setSuggestionsState(suggestions) {
    if (suggestions === undefined || suggestions === null) {
      this.setState({
        suggestions: []
      });
    } else {
      this.setState({
        suggestions
      });
    }
  }

  onSuggestionsFetchRequested = ({ value }) => {
    clearTimeout(this.timer);
    const { currentValue, currentContext } = this.state;
    const { minCharacters, category } = this.props;

    if (value === currentValue) {
      return;
    }

    if (minCharacters <= 0 || value.length >= minCharacters) {
      this.timer = setTimeout(() => {
        getSuggestions(category, currentContext, value, this.setSuggestionsState);
      }, WAIT_INTERVAL);
    }
  };

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: []
    });
  };

  render() {
    const { id, input, name, placeholder, className, required, disabled } = this.props;
    const { currentValue, suggestions } = this.state;
    const inputProps = {
      id,
      type: 'search',
      name: input != null ? input.name : name,
      placeholder,
      value: typeof currentValue === 'object' ? currentValue.text : currentValue,
      required,
      onChange: this.onChange,
      className: `form-input ${ className }`,
      disabled
    };

    return <div>
      <Field
        component={ AutoSuggestWrapper }
        name={ name }
        required
        value={ currentValue }
        suggestions={ suggestions }
        onSuggestionSelected={ this.onSuggestionSelected }
        onSuggestionsFetchRequested={ this.onSuggestionsFetchRequested }
        onSuggestionsClearRequested={ this.onSuggestionsClearRequested }
        renderSuggestion={ renderSuggestion }
        inputProps={ inputProps }
      />
    </div>;
  }
}

export default AutoCompleteInput;
